import { useAction, useConvexAuth, useMutation, useQuery } from "convex/react";
import { useCallback, useState } from "react";

import {
  InvitationAdmin,
  InvitationLanding,
  type InvitationAdminOutcome,
  type InvitationLandingState,
} from "../features/invitations";
import { useConnectivity } from "../offline/offline";
import {
  acceptInvitationAction,
  invitationListQuery,
  issueInvitationMutation,
  resendInvitationMutation,
  revokeInvitationMutation,
  type InvitationListResult,
} from "./invitations";

type InvitationId = InvitationListResult["invitations"][number]["invitationId"];

export function InvitationAdminRoute({
  circleId,
  headingLevel = 1,
}: {
  readonly circleId: string;
  readonly headingLevel?: 1 | 2;
}) {
  const invitations = useQuery(invitationListQuery, { circleId });
  const issue = useMutation(issueInvitationMutation);
  const resend = useMutation(resendInvitationMutation);
  const revoke = useMutation(revokeInvitationMutation);
  const { actionsPaused } = useConnectivity();
  const [outcome, setOutcome] = useState<InvitationAdminOutcome>("idle");

  const onIssue = useCallback(
    async (chosenName: string, email: string) => {
      if (actionsPaused) {
        setOutcome("unavailable");
        return;
      }
      setOutcome("working");
      try {
        const result = await issue({
          circleId,
          chosenName,
          email,
          idempotencyKey: crypto.randomUUID(),
        });
        if (result.status !== "applied") setOutcome("sent");
        else setOutcome(result.value.ok ? "sent" : result.value.code);
      } catch {
        setOutcome("unavailable");
      }
    },
    [actionsPaused, circleId, issue],
  );

  const onResend = useCallback(
    async (invitationId: InvitationId) => {
      if (actionsPaused) {
        setOutcome("unavailable");
        return;
      }
      setOutcome("working");
      try {
        const result = await resend({ circleId, invitationId });
        setOutcome(result.status === "applied" && result.value.ok ? "sent" : "unavailable");
      } catch {
        setOutcome("unavailable");
      }
    },
    [actionsPaused, circleId, resend],
  );

  const onRevoke = useCallback(
    async (invitationId: InvitationId) => {
      if (actionsPaused) {
        setOutcome("unavailable");
        return;
      }
      setOutcome("working");
      try {
        const result = await revoke({ circleId, invitationId });
        setOutcome(result.status === "applied" && result.value.ok ? "sent" : "unavailable");
      } catch {
        setOutcome("unavailable");
      }
    },
    [actionsPaused, circleId, revoke],
  );

  if (invitations === undefined) return null;
  return (
    <InvitationAdmin
      actionsDisabled={actionsPaused}
      invitations={invitations.invitations}
      headingLevel={headingLevel}
      onIssue={onIssue}
      onResend={onResend}
      onRevoke={onRevoke}
      outcome={outcome}
    />
  );
}

export function InvitationRoute({
  token,
  onAccepted,
}: {
  readonly token: string | null;
  readonly onAccepted: (publicPlanRef: string) => void;
}) {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const accept = useAction(acceptInvitationAction);
  const [state, setState] = useState<InvitationLandingState>(token === null ? "private" : "ready");

  const onAccept = useCallback(async () => {
    if (!isAuthenticated || token === null) {
      setState("private");
      return;
    }
    setState("working");
    try {
      const result = await accept({ admissionFlow: "invitation", token });
      if (result.status === "accepted") {
        setState("accepted");
        onAccepted(result.publicPlanRef);
      } else {
        setState(result.status === "expired" ? "expired" : "private");
      }
    } catch {
      setState("private");
    }
  }, [accept, isAuthenticated, onAccepted, token]);

  return (
    <InvitationLanding
      actionsDisabled={isLoading || !isAuthenticated}
      onAccept={onAccept}
      state={state}
    />
  );
}
